import { useState, useEffect } from 'react';
import { apiGet } from '@/lib/api-client';

interface AnalyticsData {
  userMetrics: {
    monthlyActiveUsers: number;
    totalUsers: number;
    avgLevel: string;
    avgEngagementScore: string;
  };
  llmMetrics: {
    tokensThisMonth: number;
    avgTokensPerChat: number;
    costThisMonth: string;
    tokenBreakdown: {
      systemPrompt: number;
      query: number;
      rag: number;
    };
  };
  topUsers: Array<{
    email: string;
    displayName: string;
    totalXp: number;
    level: number;
  }>;
  bottomUsers: Array<{
    email: string;
    displayName: string;
    totalXp: number;
    level: number;
  }>;
}

interface RankedUser {
  rank: string;
  email: string;
  displayName: string;
  totalXp: number;
  level: number;
  totalCompletions: number;
  longestStreak: number;
}

export function AnalyticsDashboard() {
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fullRanking, setFullRanking] = useState<RankedUser[] | null>(null);
  const [rankingLoading, setRankingLoading] = useState(false);
  const [showFullRanking, setShowFullRanking] = useState(false);

  useEffect(() => {
    loadAnalytics();
  }, []);

  async function loadAnalytics() {
    try {
      setLoading(true);
      setError(null);
      const result = await apiGet<AnalyticsData>('/api/admin/analytics');
      setData(result);
    } catch (err) {
      console.error('Error loading analytics:', err);
      setError('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  }

  async function handleToggleFullRanking() {
    if (showFullRanking) {
      setShowFullRanking(false);
      return;
    }

    if (!fullRanking) {
      try {
        setRankingLoading(true);
        const result = await apiGet<RankedUser[]>('/api/admin/analytics/full-ranking');
        setFullRanking(result);
      } catch (err) {
        console.error('Error loading full ranking:', err);
      } finally {
        setRankingLoading(false);
      }
    }

    setShowFullRanking(true);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin w-10 h-10 border-4 border-amber-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="p-6 text-center">
        <p className="text-red-400">{error || 'No data available'}</p>
        <button
          onClick={loadAnalytics}
          className="mt-4 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  const { userMetrics, llmMetrics, topUsers, bottomUsers } = data;
  const { tokenBreakdown } = llmMetrics;
  const totalTokenParts = tokenBreakdown.systemPrompt + tokenBreakdown.query + tokenBreakdown.rag;
  const systemPromptPct = totalTokenParts > 0 ? (tokenBreakdown.systemPrompt / totalTokenParts) * 100 : 0;
  const queryPct = totalTokenParts > 0 ? (tokenBreakdown.query / totalTokenParts) * 100 : 0;
  const ragPct = totalTokenParts > 0 ? (tokenBreakdown.rag / totalTokenParts) * 100 : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-bold text-white mb-2">Analytics Dashboard</h2>
        <p className="text-slate-400 text-sm">Platform usage metrics and user rankings</p>
      </div>

      {/* Top Metrics Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Monthly Active Users */}
        <div className="glass-strong p-5 rounded-xl">
          <p className="text-sm text-slate-400 mb-1">Monthly Active Users</p>
          <p className="text-3xl font-bold text-white">
            {userMetrics.monthlyActiveUsers.toLocaleString()}
          </p>
          <p className="text-xs text-slate-500 mt-1">
            of {userMetrics.totalUsers.toLocaleString()} total
          </p>
        </div>

        {/* Average Level */}
        <div className="glass-strong p-5 rounded-xl">
          <p className="text-sm text-slate-400 mb-1">Avg. Level</p>
          <p className="text-3xl font-bold text-white">
            {parseFloat(userMetrics.avgLevel).toFixed(1)}
          </p>
          <p className="text-xs text-slate-500 mt-1">across all users</p>
        </div>

        {/* Tokens This Month */}
        <div className="glass-strong p-5 rounded-xl">
          <p className="text-sm text-slate-400 mb-1">Tokens This Month</p>
          <p className="text-3xl font-bold text-white">
            {llmMetrics.tokensThisMonth.toLocaleString()}
          </p>
          <p className="text-xs text-slate-500 mt-1">
            ${llmMetrics.costThisMonth} estimated cost
          </p>
        </div>

        {/* Avg Engagement Score */}
        <div className="glass-strong p-5 rounded-xl">
          <p className="text-sm text-slate-400 mb-1">Avg. Engagement Score</p>
          <p className="text-3xl font-bold text-white">
            {parseFloat(userMetrics.avgEngagementScore).toFixed(1)}
          </p>
          <p className="text-xs text-slate-500 mt-1">
            {llmMetrics.avgTokensPerChat.toLocaleString()} avg tokens/chat
          </p>
        </div>
      </div>

      {/* Token Breakdown Section */}
      <div className="glass-strong p-6 rounded-xl">
        <h3 className="text-lg font-semibold text-white mb-4">Token Usage Breakdown</h3>

        {/* Stacked Horizontal Bar */}
        <div className="w-full h-8 rounded-lg overflow-hidden flex bg-charcoal-800">
          {systemPromptPct > 0 && (
            <div
              className="h-full bg-amber-500 transition-all duration-500"
              style={{ width: `${systemPromptPct}%` }}
              title={`System Prompt: ${systemPromptPct.toFixed(1)}%`}
            />
          )}
          {queryPct > 0 && (
            <div
              className="h-full bg-purple-500 transition-all duration-500"
              style={{ width: `${queryPct}%` }}
              title={`Query: ${queryPct.toFixed(1)}%`}
            />
          )}
          {ragPct > 0 && (
            <div
              className="h-full bg-blue-500 transition-all duration-500"
              style={{ width: `${ragPct}%` }}
              title={`RAG Context: ${ragPct.toFixed(1)}%`}
            />
          )}
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-6 mt-4">
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-sm bg-amber-500" />
            <span className="text-sm text-slate-300">
              System Prompt — {systemPromptPct.toFixed(1)}%
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-sm bg-purple-500" />
            <span className="text-sm text-slate-300">
              Query — {queryPct.toFixed(1)}%
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-sm bg-blue-500" />
            <span className="text-sm text-slate-300">
              RAG Context — {ragPct.toFixed(1)}%
            </span>
          </div>
        </div>
      </div>

      {/* Two-Column User Rankings */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top 10 Users */}
        <div className="glass-strong rounded-xl overflow-hidden">
          <div className="p-4 bg-charcoal-800/50 border-b border-charcoal-700/50">
            <h3 className="font-semibold text-white">Top 10 Users</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-charcoal-700/50">
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Rank
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-400 uppercase tracking-wider">
                    XP
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Level
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-charcoal-700/50">
                {topUsers.map((user, i) => (
                  <tr key={user.email} className="hover:bg-charcoal-800/30 transition-colors">
                    <td className="px-4 py-3 text-slate-300 font-medium">{i + 1}</td>
                    <td className="px-4 py-3 text-white truncate max-w-[200px]" title={user.email}>
                      {user.email}
                    </td>
                    <td className="px-4 py-3 text-right text-slate-300">
                      {user.totalXp.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right text-slate-300">{user.level}</td>
                  </tr>
                ))}
                {topUsers.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-slate-400">
                      No user data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Bottom 10 Users */}
        <div className="glass-strong rounded-xl overflow-hidden">
          <div className="p-4 bg-charcoal-800/50 border-b border-charcoal-700/50">
            <h3 className="font-semibold text-white">Bottom 10 Users</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-charcoal-700/50">
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Rank
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-400 uppercase tracking-wider">
                    XP
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Level
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-charcoal-700/50">
                {bottomUsers.map((user, i) => (
                  <tr key={user.email} className="hover:bg-charcoal-800/30 transition-colors">
                    <td className="px-4 py-3 text-slate-300 font-medium">{i + 1}</td>
                    <td className="px-4 py-3 text-white truncate max-w-[200px]" title={user.email}>
                      {user.email}
                    </td>
                    <td className="px-4 py-3 text-right text-slate-300">
                      {user.totalXp.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right text-slate-300">{user.level}</td>
                  </tr>
                ))}
                {bottomUsers.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-slate-400">
                      No user data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Full Ranking (Expandable) */}
      <div className="glass-strong rounded-xl overflow-hidden">
        <div className="p-4 bg-charcoal-800/50 border-b border-charcoal-700/50 flex items-center justify-between">
          <h3 className="font-semibold text-white">Full User Ranking</h3>
          <button
            onClick={handleToggleFullRanking}
            disabled={rankingLoading}
            className="px-4 py-2 text-sm font-medium rounded-lg transition-colors bg-charcoal-700 hover:bg-charcoal-600 text-slate-300 disabled:opacity-50"
          >
            {rankingLoading ? (
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
                Loading...
              </span>
            ) : showFullRanking ? (
              'Hide Ranking'
            ) : (
              'View Full Ranking'
            )}
          </button>
        </div>

        {showFullRanking && fullRanking && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-charcoal-700/50">
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Rank
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Display Name
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-400 uppercase tracking-wider">
                    XP
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Level
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Completions
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Longest Streak
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-charcoal-700/50">
                {fullRanking.map((user) => (
                  <tr key={user.email} className="hover:bg-charcoal-800/30 transition-colors">
                    <td className="px-4 py-3 text-slate-300 font-medium">{user.rank}</td>
                    <td className="px-4 py-3 text-white truncate max-w-[200px]" title={user.email}>
                      {user.email}
                    </td>
                    <td className="px-4 py-3 text-slate-300 truncate max-w-[160px]" title={user.displayName}>
                      {user.displayName || '—'}
                    </td>
                    <td className="px-4 py-3 text-right text-slate-300">
                      {user.totalXp.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right text-slate-300">{user.level}</td>
                    <td className="px-4 py-3 text-right text-slate-300">
                      {user.totalCompletions.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right text-slate-300">
                      {user.longestStreak.toLocaleString()}
                    </td>
                  </tr>
                ))}
                {fullRanking.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-slate-400">
                      No ranking data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
